import { useState, useRef, useEffect } from "react";
import { FiChevronDown, FiLogOut, FiUser } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";

interface UserMenuProps {
    userImage?: string;
    userInitials?: string;
    avatarSeed?: string;
    label?: string;
    subtitle?: string;
    triggerClassName?: string;
    onProfile: () => void;
    onLogout: () => void;
}

export const UserMenu = ({
    userImage,
    userInitials,
    avatarSeed,
    label,
    subtitle,
    triggerClassName = "",
    onProfile,
    onLogout,
}: UserMenuProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (isOpen) menuRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    }, [isOpen]);

    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("button[role='menuitem']") ?? []);
        const current = items.indexOf(document.activeElement as HTMLButtonElement);
        if (event.key === "Escape") {
            event.preventDefault();
            setIsOpen(false);
            triggerRef.current?.focus();
            return;
        }
        if (items.length === 0) return;
        if (event.key === "Home" || event.key === "End") {
            event.preventDefault();
            items[event.key === "Home" ? 0 : items.length - 1]?.focus();
            return;
        }
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
        event.preventDefault();
        const next = current < 0
            ? (event.key === "ArrowDown" ? 0 : items.length - 1)
            : (current + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
        items[next]?.focus();
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="relative" ref={menuRef}>
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-controls={isOpen ? "user-menu-options" : undefined}
                aria-label={t("common.navigation.userMenu", { defaultValue: "Account menu" })}
                className={[
                    "app-focus-ring inline-flex min-h-11 min-w-11 items-center justify-center gap-3 rounded-full transition",
                    triggerClassName || "px-2 py-1",
                ].join(" ")}
            >
                {userImage ? (
                    <img
                        src={userImage}
                        alt={'User'}
                        className="h-11 w-11 cursor-pointer rounded-full object-cover transition-opacity hover:opacity-80"
                    />
                ) : (
                    <ProfileAvatar initials={userInitials ? userInitials : "U"} seed={avatarSeed} size="menu" />
                )}

                {label || subtitle ? (
                    <>
                        <span className="hidden min-w-0 lg:flex flex-col items-start text-left">
                            {subtitle ? (
                                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--app-muted-text)]">
                                    {subtitle}
                                </span>
                            ) : null}
                            {label ? (
                                <span className="max-w-40 truncate text-sm font-medium text-[var(--app-text)]">
                                    {label}
                                </span>
                            ) : null}
                        </span>
                        <FiChevronDown className="hidden h-4 w-4 lg:block text-[var(--app-muted-text)]" />
                    </>
                ) : null}
            </button>

            {isOpen && (
                <div
                    ref={menuRef}
                    id="user-menu-options"
                    role="menu"
                    aria-label={t("common.navigation.userMenu", { defaultValue: "Account menu" })}
                    onKeyDown={handleMenuKeyDown}
                    className="app-nav-menu absolute right-0 z-50 mt-2 w-48 rounded-[var(--ds-radius-lg)] border-[1.5px] border-[var(--ds-line)] bg-[var(--ds-surface-raised)] p-2"
                >
                    <button
                        onClick={() => {
                            setIsOpen(false);
                            triggerRef.current?.focus();
                            onProfile();
                        }}
                        role="menuitem"
                        className="app-focus-ring flex min-h-11 w-full items-center gap-2 rounded-[var(--ds-radius-md)] px-3 text-left text-sm text-[var(--ds-ink)] transition-colors hover:bg-[var(--ds-ground)]"
                    >
                        <FiUser className="h-4 w-4" />
                        {t("common.actions.profile", { defaultValue: "Profile" })}
                    </button>
                    <button
                        onClick={() => {
                            setIsOpen(false);
                            triggerRef.current?.focus();
                            onLogout();
                        }}
                        role="menuitem"
                        className="app-focus-ring flex min-h-11 w-full items-center gap-2 rounded-[var(--ds-radius-md)] px-3 text-left text-sm text-[var(--ds-ink)] transition-colors hover:bg-[var(--ds-ground)]"
                    >
                        <FiLogOut className="h-4 w-4" />
                        {t("common.actions.logout")}
                    </button>
                </div>
            )}
        </div>
    );
};
